document.documentElement.classList.remove('no-js');
document.documentElement.classList.add('js');

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class PuffleDialog {
  constructor(dialog) {
    this.dialog = dialog;
    this.opener = null;
    this.onKeydown = this.onKeydown.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
    this.dialog.addEventListener('keydown', this.onKeydown);
    this.dialog.addEventListener('close', this.onClose);
    this.dialog.addEventListener('click', this.onBackdropClick);
    this.dialog.querySelectorAll('[data-dialog-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });
  }

  open(opener, initialFocus = null) {
    document.querySelectorAll('dialog[data-puffle-dialog][open]').forEach((openDialog) => {
      if (openDialog !== this.dialog) controllerFor(openDialog).close();
    });
    this.opener = opener || document.activeElement;
    this.dialog.querySelectorAll('[data-facets-form]').forEach((form) => {
      form.reset();
      form.querySelectorAll('input[type="number"]').forEach((input) => { input.disabled = false; });
      form.querySelectorAll('select[name="sort_by"]').forEach((select) => { select.disabled = false; });
      form.querySelectorAll('[data-facet-price-error]').forEach((message) => { message.hidden = true; });
    });
    if (!this.dialog.open) this.dialog.showModal();
    this.syncExpanded(true);
    document.documentElement.classList.add('drawer-open');
    document.body.classList.add('drawer-open');
    requestAnimationFrame(() => {
      const requestedFocus = initialFocus?.isConnected && this.dialog.contains(initialFocus) ? initialFocus : null;
      const initial = requestedFocus || this.dialog.querySelector('[autofocus], [data-initial-focus]') || this.focusable()[0];
      initial?.focus({ preventScroll: true });
    });
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  focusable() {
    return [...this.dialog.querySelectorAll(focusableSelector)].filter((element) => {
      if (element.matches('[tabindex="-1"], [aria-hidden="true"]') || element.closest('[aria-hidden="true"]')) return false;
      if (element.hidden || element.getClientRects().length === 0) return false;
      const closedDisclosure = element.closest('details:not([open])');
      if (closedDisclosure && element.tagName !== 'SUMMARY') return false;
      const style = window.getComputedStyle(element);
      return style.visibility !== 'hidden' && style.display !== 'none';
    });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      const predictiveSearch = this.dialog.querySelector('puffle-predictive-search');
      const predictiveInput = predictiveSearch?.querySelector('input[aria-expanded="true"]');
      const predictiveResults = predictiveSearch?.querySelector('[data-predictive-results]');
      if (predictiveInput && predictiveResults && !predictiveResults.hidden) {
        event.preventDefault();
        event.stopPropagation();
        predictiveSearch.closeResults?.(false);
        predictiveInput.focus({ preventScroll: true });
        return;
      }
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const elements = this.focusable();
    if (!elements.length) return;
    const currentIndex = elements.indexOf(document.activeElement);
    const fallbackIndex = event.shiftKey ? 0 : -1;
    const nextIndex = event.shiftKey
      ? (currentIndex < 0 ? fallbackIndex : currentIndex) - 1
      : (currentIndex < 0 ? fallbackIndex : currentIndex) + 1;
    const wrappedIndex = (nextIndex + elements.length) % elements.length;
    event.preventDefault();
    elements[wrappedIndex].focus();
  }

  onBackdropClick(event) {
    if (event.target === this.dialog) this.close();
  }

  syncExpanded(expanded) {
    if (!this.dialog.id) return;
    document.querySelectorAll(`[data-dialog-trigger][aria-controls="${CSS.escape(this.dialog.id)}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  }

  onClose() {
    this.dialog.querySelectorAll('puffle-predictive-search').forEach((search) => search.closeResults?.(false));
    this.syncExpanded(false);
    if (!document.querySelector('dialog[data-puffle-dialog][open]')) {
      document.documentElement.classList.remove('drawer-open');
      document.body.classList.remove('drawer-open');
    }
    const opener = this.opener;
    this.opener = null;
    requestAnimationFrame(() => {
      if (document.querySelector('dialog[data-puffle-dialog][open]')) return;
      if (!opener?.isConnected || opener.getClientRects().length === 0) return;
      opener.focus({ preventScroll: true });
    });
  }
}

const dialogControllers = new WeakMap();

function controllerFor(dialog) {
  if (!dialogControllers.has(dialog)) dialogControllers.set(dialog, new PuffleDialog(dialog));
  return dialogControllers.get(dialog);
}

function bindDialogTriggers(root = document) {
  root.querySelectorAll('[data-dialog-trigger]').forEach((trigger) => {
    if (trigger.dataset.dialogBound === 'true') return;
    trigger.dataset.dialogBound = 'true';
    trigger.addEventListener('click', (event) => {
      const dialog = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!(dialog instanceof HTMLDialogElement)) return;
      event.preventDefault();
      controllerFor(dialog).open(trigger);
    });
  });

  root.querySelectorAll('dialog[data-puffle-dialog]').forEach((dialog) => controllerFor(dialog));
}

function bindQuantity(root = document) {
  root.querySelectorAll('[data-quantity]').forEach((quantity) => {
    if (quantity.dataset.quantityBound === 'true') return;
    quantity.dataset.quantityBound = 'true';
    const input = quantity.querySelector('input[type="number"]');
    let drawerUpdateTimer = null;
    const updateDrawerCart = () => {
      const drawer = input?.closest('#CartDrawer');
      if (!drawer || !input?.dataset.index) return;
      const activeControl = quantity.contains(document.activeElement) ? document.activeElement : input;
      const focusTarget = {
        lineKey: quantity.dataset.lineKey || '',
        lineIndex: Number(input.dataset.index),
        quantityChange: activeControl?.dataset.quantityChange || '',
      };
      clearTimeout(drawerUpdateTimer);
      drawerUpdateTimer = window.setTimeout(async () => {
        quantity.setAttribute('aria-busy', 'true');
        quantity.querySelectorAll('button, input').forEach((control) => { control.disabled = true; });
        try {
          const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: Number(input.dataset.index), quantity: Number(input.value) }),
          });
          if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
          const updatedCart = await response.json();
          const refreshedCart = await refreshCart(false, null, focusTarget);
          const itemCount = Number(refreshedCart?.count ?? updatedCart.item_count ?? 0);
          announceLiveStatus(`Cart updated. ${itemCount} ${itemCount === 1 ? 'item' : 'items'} in your Cart.`);
        } catch (error) {
          window.location.assign(window.Shopify.routes.root + 'cart');
        }
      }, 240);
    };
    if (input?.closest('#CartDrawer')) input.addEventListener('change', updateDrawerCart);
    quantity.querySelectorAll('[data-quantity-change]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!input) return;
        const direction = Number(button.dataset.quantityChange);
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 0;
        const max = input.max ? Number(input.max) : Infinity;
        input.value = String(Math.min(max, Math.max(min, Number(input.value || 0) + direction * step)));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });
}

function announceLiveStatus(message) {
  const status = document.getElementById('PuffleLiveStatus');
  if (!status || !message) return;
  status.textContent = '';
  requestAnimationFrame(() => { status.textContent = message; });
}

function bindFacetForms(root = document) {
  root.querySelectorAll('[data-facets-form]').forEach((form) => {
    if (form.dataset.facetsBound === 'true') return;
    form.dataset.facetsBound = 'true';

    form.querySelectorAll('[data-price-range]').forEach((range) => {
      const inputs = [...range.querySelectorAll('input[type="number"]')];
      const error = range.querySelector('[data-facet-price-error]');
      inputs.forEach((input) => {
        input.addEventListener('input', () => {
          inputs.forEach((rangeInput) => {
            rangeInput.disabled = false;
            rangeInput.setCustomValidity('');
          });
          if (error) error.hidden = true;
        });
      });
    });

    form.addEventListener('submit', (event) => {
      let invalidInput = null;
      form.querySelectorAll('[data-price-range]').forEach((range) => {
        const inputs = [...range.querySelectorAll('input[type="number"]')];
        const minimum = inputs[0];
        const maximum = inputs[1];
        const error = range.querySelector('[data-facet-price-error]');
        const minimumValue = minimum?.value === '' ? null : Number(minimum.value);
        const maximumValue = maximum?.value === '' ? null : Number(maximum.value);
        const invalid = minimumValue !== null && maximumValue !== null && minimumValue > maximumValue;

        maximum?.setCustomValidity(invalid ? (error?.textContent.trim() || 'Invalid price range') : '');
        if (error) error.hidden = !invalid;
        if (invalid && !invalidInput) invalidInput = maximum;
      });

      if (invalidInput) {
        event.preventDefault();
        invalidInput.focus();
        invalidInput.reportValidity();
        return;
      }

      form.querySelectorAll('input[type="number"]').forEach((input) => {
        input.disabled = input.value.trim() === '';
      });
      form.querySelectorAll('select[name="sort_by"][data-default-value]').forEach((select) => {
        select.disabled = select.value === select.dataset.defaultValue;
      });

      if ([...new FormData(form).entries()].length === 0) {
        event.preventDefault();
        form.querySelectorAll('input[type="number"], select[name="sort_by"]').forEach((control) => { control.disabled = false; });
        const dialog = form.closest('dialog[data-puffle-dialog]');
        if (dialog) controllerFor(dialog).close();
      }
    });
  });
}

class PufflePredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-predictive-results]');
    if (!this.input || !this.results || this.bound) return;
    this.bound = true;
    this.abortController = null;
    this.requestSequence = 0;
    this.timer = null;
    this.options = [];
    this.activeIndex = -1;
    this.input.addEventListener('input', () => {
      this.cancelPendingSearch();
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.search();
      }, 180);
    });
    this.input.addEventListener('keydown', (event) => this.onKeydown(event));
  }

  disconnectedCallback() {
    this.cancelPendingSearch();
  }

  cancelPendingSearch() {
    clearTimeout(this.timer);
    this.timer = null;
    this.abortController?.abort();
    this.abortController = null;
    this.requestSequence += 1;
    this.removeAttribute('aria-busy');
  }

  syncOptions() {
    this.options = [...this.results.querySelectorAll('[role="option"]')];
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.options.forEach((option) => {
      option.classList.remove('is-active');
      option.setAttribute('aria-selected', 'false');
    });
  }

  setActive(index) {
    if (!this.options.length) return;
    this.activeIndex = (index + this.options.length) % this.options.length;
    this.options.forEach((option, optionIndex) => {
      const active = optionIndex === this.activeIndex;
      option.classList.toggle('is-active', active);
      option.setAttribute('aria-selected', String(active));
    });
    const active = this.options[this.activeIndex];
    if (active.id) this.input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      if (this.results.hidden || this.input.getAttribute('aria-expanded') !== 'true') return;
      event.preventDefault();
      event.stopPropagation();
      this.closeResults(false);
      return;
    }
    if (!this.options.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.input.setAttribute('aria-expanded', 'true');
      this.results.hidden = false;
      this.setActive(this.activeIndex + 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.input.setAttribute('aria-expanded', 'true');
      this.results.hidden = false;
      this.setActive(this.activeIndex < 0 ? this.options.length - 1 : this.activeIndex - 1);
    } else if (event.key === 'Home' && this.activeIndex >= 0) {
      event.preventDefault();
      this.setActive(0);
    } else if (event.key === 'End' && this.activeIndex >= 0) {
      event.preventDefault();
      this.setActive(this.options.length - 1);
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.options[this.activeIndex].click();
    }
  }

  closeResults(clear = false) {
    if (!this.input || !this.results) return;
    this.cancelPendingSearch();
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    this.options.forEach((option) => {
      option.classList.remove('is-active');
      option.setAttribute('aria-selected', 'false');
    });
    this.results.hidden = true;
    if (clear) {
      this.results.replaceChildren();
      this.options = [];
    }
  }

  async search() {
    const query = this.input.value.trim();
    if (query.length < 2) {
      this.closeResults(true);
      return;
    }
    const requestSequence = ++this.requestSequence;
    this.abortController?.abort();
    const abortController = new AbortController();
    this.abortController = abortController;
    const url = `${this.dataset.url}?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,page,article&resources[limit]=6`;
    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(url, { signal: abortController.signal });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const nextResults = documentFragment.querySelector('[data-predictive-results]');
      if (requestSequence !== this.requestSequence) return;
      this.results.innerHTML = nextResults?.innerHTML || '';
      this.syncOptions();
      const hasContent = Boolean(this.results.innerHTML.trim());
      this.results.hidden = !hasContent;
      this.input.setAttribute('aria-expanded', String(hasContent));
    } catch (error) {
      if (error.name !== 'AbortError' && requestSequence === this.requestSequence) {
        this.closeResults(true);
      }
    } finally {
      if (requestSequence === this.requestSequence) {
        this.removeAttribute('aria-busy');
        if (this.abortController === abortController) this.abortController = null;
      }
    }
  }
}

if (!customElements.get('puffle-predictive-search')) {
  customElements.define('puffle-predictive-search', PufflePredictiveSearch);
}

class PuffleRotator extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('[data-rotator-item]')];
    this.control = this.querySelector('[data-rotator-control]');
    this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
    this.paused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.interval = Number(this.dataset.interval) || 6000;
    this.control?.addEventListener('click', () => {
      this.paused = !this.paused;
      this.updateControl();
      this.paused ? this.stop() : this.start();
    });
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => !this.paused && this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => !this.paused && this.start());
    this.updateControl();
    if (!this.paused && this.items.length > 1) this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    if (this.items.length < 2) return;
    this.timer = window.setInterval(() => this.show((this.index + 1) % this.items.length), this.interval);
  }

  stop() {
    window.clearInterval(this.timer);
  }

  show(index) {
    this.items.forEach((item, itemIndex) => {
      const active = itemIndex === index;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', String(!active));
    });
    this.index = index;
  }

  updateControl() {
    if (!this.control) return;
    const label = this.paused ? this.control.dataset.playLabel : this.control.dataset.pauseLabel;
    this.control.setAttribute('aria-label', label || 'Pause');
    this.control.dataset.state = this.paused ? 'paused' : 'playing';
  }
}

if (!customElements.get('puffle-rotator')) customElements.define('puffle-rotator', PuffleRotator);

function findCartRefreshFocus(drawer, target) {
  if (!target) return null;
  let quantity = null;
  if (target.lineKey) {
    quantity = drawer.querySelector(`[data-quantity][data-line-key="${CSS.escape(target.lineKey)}"]`);
  }
  if (!quantity) {
    const quantities = [...drawer.querySelectorAll('[data-quantity]')];
    const fallbackIndex = Math.min(Math.max((target.lineIndex || 1) - 1, 0), quantities.length - 1);
    quantity = quantities[fallbackIndex] || null;
  }
  if (!quantity) return null;
  if (target.quantityChange) {
    const changeButton = quantity.querySelector(`[data-quantity-change="${CSS.escape(target.quantityChange)}"]`);
    if (changeButton) return changeButton;
  }
  return quantity.querySelector('input[type="number"]') || quantity.querySelector('button');
}

async function refreshCart(open = false, opener = null, focusTarget = null, preserveOpen = true) {
  const current = document.getElementById('CartDrawer');
  if (!current) return;
  const sectionId = current.dataset.sectionId || 'cart-drawer';
  const currentController = controllerFor(current);
  const preservedOpener = opener || currentController.opener;
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart?section_id=${encodeURIComponent(sectionId)}`);
    if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const next = parsed.getElementById('CartDrawer');
    if (!next) throw new Error('Cart drawer section missing');
    const wasOpen = current.open;
    current.replaceWith(next);
    const count = next.dataset.cartItemCount;
    if (count !== undefined) {
      document.querySelectorAll('[data-cart-count]').forEach((element) => { element.textContent = count; });
      document.querySelectorAll('[data-cart-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-label', `Open Cart with ${count} items`);
      });
    }
    bindTheme(next.parentElement || document);
    if (open || (preserveOpen && wasOpen)) {
      const visibleCartTrigger = Array.from(document.querySelectorAll('[data-cart-trigger]'))
        .find((trigger) => trigger.offsetParent !== null && !trigger.hidden);
      const returnTarget = preservedOpener?.isConnected ? preservedOpener : visibleCartTrigger || document.querySelector('[data-cart-trigger]');
      controllerFor(next).open(returnTarget, findCartRefreshFocus(next, focusTarget));
    } else if (wasOpen && !document.querySelector('dialog[data-puffle-dialog][open]')) {
      document.documentElement.classList.remove('drawer-open');
      document.body.classList.remove('drawer-open');
    }
    return { drawer: next, count };
  } catch (error) {
    window.location.assign(window.Shopify.routes.root + 'cart');
    return null;
  }
}

function visibleAddControl() {
  return Array.from(document.querySelectorAll('[data-add-to-cart], [data-sticky-submit], [data-cart-trigger]'))
    .find((control) => !control.disabled && !control.hidden && control.getClientRects().length > 0);
}

function openCartAddConfirmation(item, opener = null) {
  const dialog = document.querySelector('[data-cart-add-confirmation]');
  if (!dialog) return false;

  const title = item?.product_title || item?.title || 'Item';
  const variantTitle = item?.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
  const imageValue = item?.featured_image?.url || item?.featured_image || item?.image || '';
  const image = dialog.querySelector('[data-added-product-image]');
  const media = dialog.querySelector('[data-added-product-media]');
  const titleTarget = dialog.querySelector('[data-added-product-title]');
  const variantTarget = dialog.querySelector('[data-added-variant-title]');

  if (titleTarget) titleTarget.textContent = title;
  if (variantTarget) {
    variantTarget.textContent = variantTitle;
    variantTarget.hidden = !variantTitle;
  }
  if (image && media) {
    if (imageValue) {
      image.src = imageValue;
      image.alt = '';
      media.hidden = false;
    } else {
      image.removeAttribute('src');
      media.hidden = true;
    }
  }

  const returnTarget = opener?.isConnected ? opener : visibleAddControl();
  controllerFor(dialog).open(returnTarget);
  announceLiveStatus(`${title} added to your Cart.`);
  return true;
}

async function handleAfterAdd({ action = 'cart', cartBehavior = 'drawer', item = null, opener = null } = {}) {
  const rootUrl = window.Shopify?.routes?.root || '/';

  if (action === 'confirmation') {
    const refreshed = await refreshCart(false, opener, null, false);
    if (refreshed) openCartAddConfirmation(item, opener);
    return;
  }

  if (action === 'stay') {
    const refreshed = await refreshCart(false, opener);
    if (refreshed) {
      const title = item?.product_title || item?.title || 'Item';
      announceLiveStatus(`${title} added to your Cart.`);
    }
    return;
  }

  if (cartBehavior === 'drawer') {
    await refreshCart(true, opener);
  } else {
    window.location.assign(`${rootUrl}cart`);
  }
}

window.PuffleCart = {
  refresh: refreshCart,
  afterAdd: handleAfterAdd,
  confirmAdded: openCartAddConfirmation,
  announce: announceLiveStatus
};

function bindTheme(root = document) {
  bindDialogTriggers(root);
  bindQuantity(root);
  bindFacetForms(root);
}

bindTheme();
document.addEventListener('shopify:section:load', (event) => bindTheme(event.target));
document.addEventListener('puffle:cart:refresh', (event) => refreshCart(Boolean(event.detail?.open), event.detail?.opener));
